/**
 * Best-effort reader for FBX GlobalSettings metadata that three's FBXLoader
 * parses but does not expose: the up axis and the authored frame rate. Used to
 * normalize Move.AI takes (Z-up, custom 180 fps). Never throws — returns {} for
 * anything unreadable.
 */
export interface FbxSettings {
  /** 0 = X, 1 = Y, 2 = Z. */
  upAxis?: number;
  /** Authored frame rate (TimeMode table or CustomFrameRate), if declared. */
  frameRate?: number;
}

/** FBX SDK EMode enum → frames per second. Mode 14 (custom) uses CustomFrameRate. */
const TIMEMODE_FPS: Record<number, number> = {
  1: 120,
  2: 100,
  3: 60,
  4: 50,
  5: 48,
  6: 30,
  7: 30, // 30 drop
  8: 29.97,
  9: 29.97,
  10: 25,
  11: 24,
  12: 1000,
  13: 23.976,
  15: 96,
  16: 72,
  17: 59.94,
};

const BINARY_MAGIC = 'Kaydara FBX Binary  ';

export function readFbxSettings(buffer: ArrayBuffer): FbxSettings {
  try {
    const bytes = new Uint8Array(buffer);
    let isBinary = bytes.length > 27;
    for (let i = 0; isBinary && i < BINARY_MAGIC.length; i++) {
      if (bytes[i] !== BINARY_MAGIC.charCodeAt(i)) isBinary = false;
    }
    const raw = isBinary ? readBinaryGlobalSettings(buffer) : readAsciiGlobalSettings(buffer);
    return interpret(raw);
  } catch {
    return {};
  }
}

interface RawSettings {
  upAxis?: number;
  timeMode?: number;
  customFrameRate?: number;
}

function interpret(raw: RawSettings): FbxSettings {
  const out: FbxSettings = {};
  if (raw.upAxis !== undefined) out.upAxis = raw.upAxis;
  if (raw.timeMode === 14) {
    if (raw.customFrameRate !== undefined && raw.customFrameRate > 0)
      out.frameRate = raw.customFrameRate;
  } else if (raw.timeMode !== undefined) {
    const fps = TIMEMODE_FPS[raw.timeMode];
    if (fps) out.frameRate = fps;
  }
  return out;
}

// --- Binary FBX ----------------------------------------------------------------
// Format: 27-byte header (magic + version), then a flat list of nested records:
// endOffset, numProperties, propertyListLen (u32, or u64 from version 7500),
// nameLen (u8), name, properties, child records, null terminator record.

interface RecordHeader {
  endOffset: number;
  numProps: number;
  propListLen: number;
  name: string;
  /** Offset of the property list (records nest after it). */
  propsOffset: number;
}

function readRecordHeader(view: DataView, offset: number, big: boolean): RecordHeader | null {
  const wordSize = big ? 8 : 4;
  if (offset + wordSize * 3 + 1 > view.byteLength) return null;
  const word = (at: number): number =>
    big ? Number(view.getBigUint64(at, true)) : view.getUint32(at, true);
  const endOffset = word(offset);
  const numProps = word(offset + wordSize);
  const propListLen = word(offset + wordSize * 2);
  const nameLen = view.getUint8(offset + wordSize * 3);
  const nameStart = offset + wordSize * 3 + 1;
  let name = '';
  for (let i = 0; i < nameLen; i++) name += String.fromCharCode(view.getUint8(nameStart + i));
  return { endOffset, numProps, propListLen, name, propsOffset: nameStart + nameLen };
}

/** Parse a record's scalar properties. Arrays and unknown types abort the list. */
function readProps(view: DataView, offset: number, count: number): unknown[] {
  const values: unknown[] = [];
  let off = offset;
  for (let i = 0; i < count; i++) {
    const type = String.fromCharCode(view.getUint8(off));
    off += 1;
    switch (type) {
      case 'C':
        values.push(view.getUint8(off) !== 0);
        off += 1;
        break;
      case 'Y':
        values.push(view.getInt16(off, true));
        off += 2;
        break;
      case 'I':
        values.push(view.getInt32(off, true));
        off += 4;
        break;
      case 'F':
        values.push(view.getFloat32(off, true));
        off += 4;
        break;
      case 'D':
        values.push(view.getFloat64(off, true));
        off += 8;
        break;
      case 'L':
        values.push(Number(view.getBigInt64(off, true)));
        off += 8;
        break;
      case 'S':
      case 'R': {
        const len = view.getUint32(off, true);
        off += 4;
        let s = '';
        for (let j = 0; j < len; j++) s += String.fromCharCode(view.getUint8(off + j));
        values.push(s);
        off += len;
        break;
      }
      default:
        // Array or unknown type — nothing we need lives past one of these.
        return values;
    }
  }
  return values;
}

function readBinaryGlobalSettings(buffer: ArrayBuffer): RawSettings {
  const view = new DataView(buffer);
  const version = view.getUint32(23, true);
  const big = version >= 7500;

  // Top level: find the GlobalSettings record.
  let off = 27;
  while (off < view.byteLength) {
    const rec = readRecordHeader(view, off, big);
    if (!rec || rec.endOffset === 0 || rec.endOffset <= off) break;
    if (rec.name === 'GlobalSettings') {
      // Its children: find Properties70, then scan the "P" records inside.
      let child = rec.propsOffset + rec.propListLen;
      while (child < rec.endOffset) {
        const c = readRecordHeader(view, child, big);
        if (!c || c.endOffset === 0 || c.endOffset <= child) break;
        if (c.name === 'Properties70') return readP70(view, c, big);
        child = c.endOffset;
      }
      return {};
    }
    off = rec.endOffset;
  }
  return {};
}

function readP70(view: DataView, container: RecordHeader, big: boolean): RawSettings {
  const raw: RawSettings = {};
  let off = container.propsOffset + container.propListLen;
  while (off < container.endOffset) {
    const p = readRecordHeader(view, off, big);
    if (!p || p.endOffset === 0 || p.endOffset <= off) break;
    if (p.name === 'P') {
      // Property record: [name, typeName, label, flags, value, ...].
      const props = readProps(view, p.propsOffset, Math.min(p.numProps, 5));
      const [name, , , , value] = props;
      if (name === 'UpAxis' && typeof value === 'number') raw.upAxis = value;
      if (name === 'TimeMode' && typeof value === 'number') raw.timeMode = value;
      if (name === 'CustomFrameRate' && typeof value === 'number') raw.customFrameRate = value;
    }
    off = p.endOffset;
  }
  return raw;
}

// --- ASCII FBX -------------------------------------------------------------------

function readAsciiGlobalSettings(buffer: ArrayBuffer): RawSettings {
  // GlobalSettings appears near the top of the file; 128 KB is generous.
  const text = new TextDecoder('utf-8', { fatal: false }).decode(
    buffer.slice(0, Math.min(buffer.byteLength, 131072))
  );
  const num = (name: string): number | undefined => {
    const m = text.match(new RegExp(`P:\\s*"${name}"(?:\\s*,\\s*"[^"]*"){3}\\s*,\\s*(-?[\\d.]+)`));
    return m ? Number(m[1]) : undefined;
  };
  return {
    upAxis: num('UpAxis'),
    timeMode: num('TimeMode'),
    customFrameRate: num('CustomFrameRate'),
  };
}
